import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  ILogger,
  LogLevel,
} from '@microsoft/signalr';
import { REACT_APP_NAME } from 'src/utils/constants/constants';
import {
  ClientLogRequest,
  LogLevel as POSLogLevel,
  PosClientConfig,
} from 'src/WebApiController';

const toPOSLogLevel = (logLevel: LogLevel): POSLogLevel => {
  if (logLevel === LogLevel.Debug) {
    return POSLogLevel.Verbose;
  }
  if (logLevel === LogLevel.Error) {
    return POSLogLevel.Error;
  }
  if (logLevel === LogLevel.Warning) {
    return POSLogLevel.Warning;
  }
  return POSLogLevel.Information;
};

/**
 * Wrap the logger with POS ClientLogClient to send sever log
 */
class SignalRLogWithSentry implements ILogger {
  constructor(
    private logFunction: (logRequest: ClientLogRequest) => void,
    private url: string
  ) {}

  log(logLevel: LogLevel, message: string) {
    if (logLevel <= LogLevel.Debug) {
      return;
    }
    this.logFunction({
      logLevel: toPOSLogLevel(logLevel),
      applicationSource: REACT_APP_NAME,
      errorId: null,
      error: message,
      pageUrl: window.location.href,
      componentStack: 'POSSignalRHub',
      timestamp: new Date().toISOString(),
      additionalInfo: {
        hubUrl: this.url,
      },
    });
  }
}

export class POSSignalRHub {
  private MaxStartRetries = 3;
  private InitialRetryDelayInMs = 500;
  private AutoConnectRetryDelays = [0, 1_000, 2_000, 10_000];
  private lockResolver?: () => void;
  private hubUrl: string;
  public connection: HubConnection;

  constructor(
    private url: string,
    posClientConfig: PosClientConfig,
    private logFunction: (logRequest: ClientLogRequest) => void // Add this parameter
  ) {
    this.hubUrl = this.url;

    this.connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl)
      .configureLogging(new SignalRLogWithSentry(this.logFunction, this.hubUrl))
      .withAutomaticReconnect(this.AutoConnectRetryDelays)
      .build();

    this.setupConnectionMonitoring();
    this.registerWebLock(this.hubUrl);
  }

  async start(): Promise<void> {
    if (this.connection.state === HubConnectionState.Connected) {
      return;
    }

    let attempt = 0;
    let delay = this.InitialRetryDelayInMs;

    while (attempt <= this.MaxStartRetries) {
      try {
        await this.connection.start();

        this.logFunction({
          logLevel: POSLogLevel.Information,
          applicationSource: REACT_APP_NAME,
          errorId: null,
          error: 'Connection started',
          pageUrl: window.location.href,
          componentStack: 'POSSignalRHub',
          timestamp: new Date().toISOString(),
          additionalInfo: {
            attempt,
            hubUrl: this.hubUrl,
            connectionId: this.connection.connectionId,
          },
        });
        return;
      } catch (err) {
        if (attempt === this.MaxStartRetries) {
          this.logFunction({
            logLevel: POSLogLevel.Error,
            applicationSource: REACT_APP_NAME,
            errorId: null,
            error: `Failed on all attempts to start SignalR hub`,
            pageUrl: window.location.href,
            componentStack: 'POSSignalRHub',
            timestamp: new Date().toISOString(),
            additionalInfo: {
              attempt,
              hubUrl: this.hubUrl,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              finalError: (err as any)?.message,
            },
          });
          throw err;
        }

        await this.delay(delay);
        delay *= 2;
        attempt++;
      }
    }
  }

  async stop(): Promise<void> {
    try {
      await this.connection.stop();

      this.logFunction({
        logLevel: POSLogLevel.Information,
        applicationSource: REACT_APP_NAME,
        errorId: null,
        error: `Connection stopped`,
        pageUrl: window.location.href,
        componentStack: 'POSSignalRHub',
        timestamp: new Date().toISOString(),
        additionalInfo: {
          hubUrl: this.hubUrl,
        },
      });
    } catch (err) {
      this.logFunction({
        logLevel: POSLogLevel.Error,
        applicationSource: REACT_APP_NAME,
        errorId: null,
        error: `Failed to stop SignalR Hub`,
        pageUrl: window.location.href,
        componentStack: 'POSSignalRHub',
        timestamp: new Date().toISOString(),
        additionalInfo: {
          hubUrl: this.hubUrl,
        },
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public on(methodName: string, newMethod: (...args: any[]) => void): void {
    this.connection.on(methodName, newMethod);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public off(methodName: string, methods?: (...args: any[]) => void): void {
    if (methods) {
      this.connection.off(methodName, methods);
    } else {
      this.connection.off(methodName);
    }
  }

  public onclose(callback: (error?: Error) => void): void {
    // Release web lock
    this.lockResolver?.();
    this.connection.onclose(callback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async invoke<T>(methodName: string, ...args: any[]): Promise<T> {
    try {
      return await this.connection.invoke<T>(methodName, ...args);
    } catch (err) {
      this.logFunction({
        logLevel: POSLogLevel.Error,
        applicationSource: REACT_APP_NAME,
        errorId: null,
        error: `Failed to invoke SignalR Hub method`,
        pageUrl: window.location.href,
        componentStack: 'POSSignalRHub',
        timestamp: new Date().toISOString(),
        additionalInfo: {
          hubUrl: this.hubUrl,
          methodName,
        },
      });
      throw err;
    }
  }

  get state(): HubConnectionState {
    return this.connection.state;
  }

  get connectionId(): string | null {
    return this.connection.connectionId;
  }

  private delay = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  private registerWebLock(hubUrl: string) {
    if (navigator && navigator.locks && navigator.locks.request) {
      const promise = new Promise<void>((res) => {
        this.lockResolver = res;
      });

      navigator.locks.request(
        `POSSignalRHub_${hubUrl}`,
        { mode: 'shared' },
        () => {
          return promise;
        }
      );
    }
  }

  /**
   * Set up connection state monitoring for better reliability
   */
  private setupConnectionMonitoring(): void {
    this.connection.onreconnecting((error?: Error) => {
      this.logFunction({
        logLevel: POSLogLevel.Warning,
        applicationSource: REACT_APP_NAME,
        errorId: null,
        error: 'SignalR connection reconnecting',
        pageUrl: window.location.href,
        componentStack: 'POSSignalRHub',
        timestamp: new Date().toISOString(),
        additionalInfo: {
          hubUrl: this.hubUrl,
          error: error?.message,
        },
      });
    });

    this.connection.onreconnected((connectionId?: string) => {
      this.logFunction({
        logLevel: POSLogLevel.Information,
        applicationSource: REACT_APP_NAME,
        errorId: null,
        error: 'SignalR connection reconnected',
        pageUrl: window.location.href,
        componentStack: 'POSSignalRHub',
        timestamp: new Date().toISOString(),
        additionalInfo: {
          hubUrl: this.hubUrl,
          newConnectionId: connectionId,
        },
      });
    });
  }
}
